/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Spin, Tooltip, notification, Dropdown, Menu } from 'antd';
import {
  SendOutlined,
  PlusOutlined,
  HistoryOutlined,
  ExpandOutlined,
  CloseOutlined,
  FilePdfOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import get from 'lodash/get';
import { buildAskEvansPdfReport } from '../../utils/askEvansPdfReport';
import { normalizeCognitoGroups } from '../../utils/userGroupUtils';
import {
  SPACER_LOG_AI_FEATURES,
  deleteAiInsightChat,
  fetchAiInsightFeatures,
  generateAiInsight,
  fetchAskEvansQuota,
  sendAskChatMessage,
  getAiInsightChat,
  getAiInsightChatSummary,
  listAiInsightChats,
  mapChatThreadToMessages,
  cacheUsageForFeedbackTarget,
  saveBlobAsFile,
  downloadAiInsightPdf,
  quotaUsagePercent,
  formatChatSessionDate,
  normalizeAskEvansConfidence,
} from '../../services/aiInsightsApi';
import SpacerLogMarkdown from './SpacerLogMarkdown';
import SpacerLogAiCitations from './SpacerLogAiCitations';
import SpacerLogAiInsightPanel from './SpacerLogAiInsightPanel';
import SpacerLogAiAnswerActions from './SpacerLogAiAnswerActions';
import SpacerLogAiQuotaBar from './SpacerLogAiQuotaBar';
import SpacerLogAiChatSidebar from './SpacerLogAiChatSidebar';

const VIEW = { ASK: 'ask', INSIGHTS: 'insights' };
const LAYOUT = { PANEL: 'panel', PAGE: 'page' };

const PANEL_SUGGESTIONS = [
  'What was joint completion rate today?',
  'Summarize downtime by station',
  'Which joints are incomplete?',
  'Compare day vs night output',
];

const normalizeShift = (shift) => {
  if (shift === '0' || shift === 0) return 0;
  if (shift === '1' || shift === 1) return 1;
  return 'both';
};

const userInitials = () => {
  try {
    const raw = localStorage.getItem('user') || localStorage.getItem('userInfo');
    if (raw) {
      const u = JSON.parse(raw);
      const name = u.firstName || u.given_name || u.name || u.email || '';
      const parts = String(name).trim().split(/\s+/);
      if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
    }
  } catch {
    /* ignore */
  }
  return 'U';
};

/**
 * Ask Evans FAB + floating panel + expandable full page — Lessons Learned sister UX.
 */
const SpacerLogAiAskBar = ({
  context = {},
  activeView = 'daily',
  open: controlledOpen,
  onOpenChange,
  view: controlledView,
  onViewChange,
  initialFeature,
  tabScope = 'daily',
  /** Force open in full-page layout (e.g. /AskEvans route). */
  defaultLayout = LAYOUT.PANEL,
}) => {
  const [meta, setMeta] = useState(null);
  const [quota, setQuota] = useState(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const [internalView, setInternalView] = useState(VIEW.ASK);
  const [layout, setLayout] = useState(defaultLayout);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [threadLoading, setThreadLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const history = useHistory();
  const abortRef = useRef(null);
  const loadAbortRef = useRef(null);
  const loadGenRef = useRef(0);
  const timerRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const chatSessionIdRef = useRef(null);
  const initials = useRef(userInitials()).current;

  const canOpenAiAdmin = (() => {
    try {
      const groups = normalizeCognitoGroups(JSON.parse(localStorage.getItem('UserGroup') || '[]'));
      // UI Super Admin → Cognito `admin`; UI Admin → Cognito `super-admin`
      return groups.includes('admin') || groups.includes('super-admin');
    } catch {
      return false;
    }
  })();

  const open = controlledOpen != null ? controlledOpen : internalOpen;
  const setOpen = (next) => {
    if (onOpenChange) onOpenChange(next);
    else setInternalOpen(next);
  };

  const view = controlledView != null ? controlledView : internalView;
  const setView = (next) => {
    if (onViewChange) onViewChange(next);
    else setInternalView(next);
  };

  const isInsights = view === VIEW.INSIGHTS;
  const isPanel = layout === LAYOUT.PANEL;
  const isRoutePage = defaultLayout === LAYOUT.PAGE;
  /** Spacer Log expand — fullscreen modal on the same screen (no /AskEvans redirect). */
  const isSpacerModal = !isRoutePage && layout === LAYOUT.PAGE;

  useEffect(() => {
    setLayout(defaultLayout);
  }, [defaultLayout]);

  useEffect(() => {
    if (open && isInsights && defaultLayout === LAYOUT.PANEL) {
      setLayout(LAYOUT.PAGE);
    }
  }, [open, isInsights, defaultLayout]);

  useEffect(() => {
    chatSessionIdRef.current = chatSessionId;
  }, [chatSessionId]);

  useEffect(() => {
    let cancelled = false;
    fetchAiInsightFeatures()
      .then((data) => {
        if (cancelled) return;
        setMeta(data);
        if (data.quota) setQuota(data.quota);
      })
      .catch(() => {
        if (!cancelled) setMeta({ enabled: false, ragConfigured: false, features: [] });
      });
    fetchAskEvansQuota()
      .then((q) => {
        if (!cancelled && q) setQuota(q);
      })
      .catch(() => { /* optional */ });
    return () => {
      cancelled = true;
      if (abortRef.current) abortRef.current.abort();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const refreshSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const data = await listAiInsightChats({
        projectId: context.projectId || undefined,
        limit: 40,
      });
      setSessions(data.sessions || []);
      if (data.quota) setQuota(data.quota);
    } catch {
      /* best-effort */
    } finally {
      setSessionsLoading(false);
    }
  }, [context.projectId]);

  useEffect(() => {
    if (open && view === VIEW.ASK) {
      refreshSessions();
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open, view, refreshSessions]);

  useEffect(() => {
    if (!open) setHistoryOpen(false);
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, generating, threadLoading]);

  const aiEnabled = meta ? meta.enabled !== false : true;
  const hasProject = context.projectId != null && context.projectId !== '';
  const usagePct = quotaUsagePercent(quota);
  const quotaExhausted = usagePct != null && usagePct >= 100;

  const buildBody = useCallback(
    (feature, q, sessionId) => {
      const body = { feature, mode: 'ai', question: q };
      if (sessionId) body.chatSessionId = sessionId;
      if (feature === SPACER_LOG_AI_FEATURES.RAG_QA) {
        if (context.projectId != null) body.projectId = context.projectId;
        return body;
      }
      body.projectId = context.projectId;
      // Spacer Log filters — same date range / pipeline as the dashboard.
      if (context.sowId != null && context.sowId !== 'all' && !Number.isNaN(Number(context.sowId))) {
        body.sowId = Number(context.sowId);
      }
      if (context.logDate) body.logDate = context.logDate;
      if (context.shift != null) body.shift = normalizeShift(context.shift);
      if (context.fromDate) body.fromDate = context.fromDate;
      if (context.toDate) body.toDate = context.toDate;
      if (context.periodLabel) body.periodLabel = context.periodLabel;
      if (context.pipelineLabel) body.pipelineLabel = context.pipelineLabel;
      if (activeView === 'project') {
        if (context.anchorDate) body.anchorDate = context.anchorDate;
        if (context.periodType) body.periodType = context.periodType;
      }
      return body;
    },
    [context, activeView]
  );

  const handleCancel = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const handleClose = () => {
    handleCancel();
    if (loadAbortRef.current) {
      try {
        loadAbortRef.current.abort();
      } catch {
        /* ignore */
      }
      loadAbortRef.current = null;
    }
    loadGenRef.current += 1;
    setThreadLoading(false);
    setHistoryOpen(false);
    setOpen(false);
    if (defaultLayout === LAYOUT.PANEL) setLayout(LAYOUT.PANEL);
  };

  const handleFabClick = () => {
    if (open) {
      handleClose();
      return;
    }
    if (!aiEnabled || quotaExhausted) return;
    setView(VIEW.ASK);
    setLayout(LAYOUT.PANEL);
    setOpen(true);
  };

  const handleExpand = () => {
    // Stay on Spacer Log — open fullscreen modal; Close returns to the same screen.
    setHistoryOpen(false);
    setLayout(LAYOUT.PAGE);
  };

  const handleNewChat = () => {
    if (abortRef.current) abortRef.current.abort();
    if (loadAbortRef.current) {
      try {
        loadAbortRef.current.abort();
      } catch {
        /* ignore */
      }
      loadAbortRef.current = null;
    }
    loadGenRef.current += 1;
    setMessages([]);
    setQuestion('');
    setChatSessionId(null);
    chatSessionIdRef.current = null;
    setGenerating(false);
    setElapsedSec(0);
    setThreadLoading(false);
    setHistoryOpen(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleSelectChat = async (id) => {
    if (id == null || id === '') return;

    // Always show Chat tab when opening a history thread.
    setView(VIEW.ASK);

    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch {
        /* ignore */
      }
    }
    if (loadAbortRef.current) {
      try {
        loadAbortRef.current.abort();
      } catch {
        /* ignore */
      }
    }
    setGenerating(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const gen = loadGenRef.current + 1;
    loadGenRef.current = gen;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    loadAbortRef.current = controller;
    const requestId = String(id);

    setHistoryOpen(false);
    setThreadLoading(true);
    setMessages([]);
    setChatSessionId(requestId);
    chatSessionIdRef.current = requestId;

    try {
      const thread = await getAiInsightChat(requestId, {
        signal: controller?.signal,
      });
      // Superseded by a newer history click.
      if (gen !== loadGenRef.current) return;

      const sessionId = thread?.session?.id != null ? String(thread.session.id) : requestId;
      setChatSessionId(sessionId);
      chatSessionIdRef.current = sessionId;
      const mapped = mapChatThreadToMessages(thread?.messages || []);
      mapped.forEach((m) => {
        if (m.role === 'assistant' && m.feedbackTargetId && m.usage) {
          cacheUsageForFeedbackTarget(m.feedbackTargetId, m.usage);
        }
      });
      setMessages(mapped);
      setQuestion('');
      if (!mapped.length) {
        notification.warning({
          message: 'Chat has no messages',
          description: 'This session opened but the server returned an empty thread.',
        });
      }
    } catch (err) {
      if (gen !== loadGenRef.current) return;
      const msg = String(get(err, 'error', get(err, 'message', err)));
      const canceled =
        err?.code === 'ERR_CANCELED'
        || err?.name === 'CanceledError'
        || err?.name === 'AbortError'
        || /cancel/i.test(msg);
      // Aborted because user clicked another chat — ignore.
      if (canceled && loadAbortRef.current !== controller) return;
      if (canceled && gen !== loadGenRef.current) return;
      setMessages([]);
      if (!canceled) {
        notification.error({
          message: 'Could not open chat',
          description: msg || 'The server did not respond.',
        });
      }
    } finally {
      if (loadAbortRef.current === controller) loadAbortRef.current = null;
      if (gen === loadGenRef.current) setThreadLoading(false);
    }
  };

  const handleDeleteChat = (id) => {
    if (!id) return;
    Modal.confirm({
      title: 'Delete this chat?',
      content: 'The full thread will be removed. This cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      zIndex: 1300,
      onOk: async () => {
        try {
          await deleteAiInsightChat(id);
          if (String(chatSessionId) === String(id)) handleNewChat();
          await refreshSessions();
          notification.success({ message: 'Chat deleted' });
        } catch (err) {
          notification.error({
            message: 'Delete failed',
            description: String(get(err, 'error', get(err, 'message', err))),
          });
        }
      },
    });
  };

  const handleSessionSummaryPdf = async () => {
    if (!chatSessionId || summaryLoading) return;
    setSummaryLoading(true);
    const stamp = new Date().toISOString().slice(0, 10);
    const fileName = `ask-evans-session-${stamp}.pdf`;
    try {
      const summary = await getAiInsightChatSummary(chatSessionId);
      const markdown =
        summary.summaryMarkdown ||
        messages
          .map((m) =>
            m.role === 'user'
              ? `### Q: ${m.text}\n`
              : `- ${(m.markdown || '').split('\n').filter(Boolean).slice(0, 3).join(' ')}`
          )
          .join('\n\n');
      const title = summary.title || 'Ask Evans Session Summary';
      let blob;
      try {
        blob = await buildAskEvansPdfReport({
          markdown,
          title,
          eyebrow: 'ASK EVANS  ·  SESSION SUMMARY',
          subtitle: 'Spacer Log — Prepared for operations · CONFIDENTIAL',
        });
      } catch (clientErr) {
        // eslint-disable-next-line no-console
        console.warn('Client session PDF failed, trying API', clientErr);
        blob = await downloadAiInsightPdf({
          markdown,
          title,
          feature: 'session_summary',
          question: title,
        });
      }
      if (!blob || !(blob instanceof Blob) || blob.size === 0) {
        throw new Error('PDF was empty.');
      }
      saveBlobAsFile(blob, fileName);
    } catch (err) {
      notification.error({
        message: 'Session summary failed',
        description: String(get(err, 'message', err)),
      });
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleSend = async (presetQuestion) => {
    const q = String(presetQuestion != null ? presetQuestion : question).trim();
    if (!q || generating) return;

    const feature = SPACER_LOG_AI_FEATURES.NL_QA;

    if (!hasProject) {
      notification.warning({ message: 'Select a project first' });
      return;
    }
    if (!aiEnabled) {
      notification.warning({ message: 'Ask Evans is temporarily unavailable.' });
      return;
    }

    const userMsg = { id: `u-${Date.now()}`, role: 'user', text: q };
    setMessages((prev) => [...prev, userMsg]);
    setQuestion('');

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setGenerating(true);
    setElapsedSec(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsedSec((s) => s + 1), 1000);

    const sessionForRequest = chatSessionIdRef.current;

    try {
      const askBody = buildBody(feature, q, sessionForRequest);
      let data;
      try {
        data = await sendAskChatMessage(askBody, { signal: controller.signal });
      } catch (asyncErr) {
        if (asyncErr?.name === 'AbortError' || controller.signal.aborted) throw asyncErr;
        data = await generateAiInsight(askBody, { signal: controller.signal });
      }
      if (data.quota) setQuota(data.quota);
      if (data.chatSessionId) {
        setChatSessionId(data.chatSessionId);
        chatSessionIdRef.current = data.chatSessionId;
      }
      if (data.feedbackTargetId && data.usage) {
        cacheUsageForFeedbackTarget(data.feedbackTargetId, data.usage);
      }
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          markdown: data.markdown || 'No response.',
          citations: data.citations,
          confidence: normalizeAskEvansConfidence(data.confidence),
          fallback: data.fallback,
          mode: data.mode,
          error: data.error,
          feature,
          question: q,
          usage: data.usage,
          feedbackTargetId: data.feedbackTargetId,
          feedbackRating: null,
          ragSessionId: data.ragSessionId || null,
        },
      ]);
      refreshSessions();
    } catch (err) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError') {
        return;
      }
      const msg = get(err, 'error', get(err, 'message', 'Failed to get an answer.'));
      setMessages((prev) => [
        ...prev,
        { id: `e-${Date.now()}`, role: 'assistant', markdown: msg, isError: true },
      ]);
    } finally {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setGenerating(false);
      abortRef.current = null;
    }
  };

  const placeholder = 'Ask a question about Spacer Log…';

  const canSummary =
    Boolean(chatSessionId) &&
    messages.some((m) => m.role === 'assistant' && !m.isError) &&
    !generating &&
    !threadLoading;

  const renderSuggestions = () => (
    <>
      <p className="spacer-ask-evans__try-label">Try asking:</p>
      <div className="spacer-ask-evans__suggestions">
        {PANEL_SUGGESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            className="spacer-ask-evans__suggestion"
            onClick={() => handleSend(s)}
            disabled={generating || !aiEnabled || quotaExhausted}
          >
            {s}
          </button>
        ))}
      </div>
    </>
  );

  const renderEmpty = () => (
    <div className={`spacer-ask-evans__empty${isPanel ? ' spacer-ask-evans__empty--panel' : ''}`}>
      {!isPanel ? (
        <>
          <div className="spacer-ask-evans__empty-icon" aria-hidden>
            <Sparkles size={28} strokeWidth={2} />
          </div>
          <div className="spacer-ask-evans__empty-title">Ask about Spacer Log</div>
          <p className="spacer-ask-evans__empty-text">
            Ask Evans answers questions about joints, downtime, cycle time, shift compliance, and
            ingested Spacer Log documents — grounded in your project filters.
          </p>
          {renderSuggestions()}
        </>
      ) : (
        renderSuggestions()
      )}
    </div>
  );

  const renderMessages = () => (
    <div className="spacer-ask-evans__messages" ref={scrollRef}>
      {threadLoading ? (
        <div className="spacer-ask-evans__empty">
          <Spin />
          <p className="spacer-ask-evans__empty-text">Loading chat…</p>
          <button
            type="button"
            className="spacer-ask-evans__link-btn"
            onClick={() => {
              if (loadAbortRef.current) {
                try {
                  loadAbortRef.current.abort();
                } catch {
                  /* ignore */
                }
                loadAbortRef.current = null;
              }
              setThreadLoading(false);
            }}
          >
            Cancel
          </button>
        </div>
      ) : null}

      {!threadLoading && !messages.length && !generating ? renderEmpty() : null}

      {!threadLoading
        ? messages.map((m) =>
            m.role === 'user' ? (
              <div key={m.id} className="spacer-ask-evans__row spacer-ask-evans__row--user">
                <div className="spacer-ask-evans__bubble spacer-ask-evans__bubble--user">{m.text}</div>
                <div className="spacer-ask-evans__avatar spacer-ask-evans__avatar--user">{initials}</div>
              </div>
            ) : (
              <div key={m.id} className="spacer-ask-evans__row spacer-ask-evans__row--bot">
                <div className="spacer-ask-evans__avatar spacer-ask-evans__avatar--ae">AE</div>
                <div
                  className={`spacer-ask-evans__bubble spacer-ask-evans__bubble--bot${
                    m.isError ? ' spacer-ask-evans__bubble--error' : ''
                  }`}
                >
                  {(m.fallback || m.mode === 'template') && !m.isError ? (
                    <div className="spacer-ask-evans__fallback">
                      {m.fallback
                        ? 'AI unavailable — showing template answer'
                        : 'Deterministic (template) answer'}
                      {m.error ? ` · ${m.error}` : ''}
                    </div>
                  ) : null}
                  <SpacerLogMarkdown content={m.markdown} />
                  <SpacerLogAiCitations citations={m.citations} />
                  {!m.isError ? (
                    <SpacerLogAiAnswerActions
                      key={`actions-${m.id}`}
                      markdown={m.markdown}
                      usage={m.usage}
                      feedbackTargetId={m.feedbackTargetId}
                      initialRating={m.feedbackRating}
                      confidence={m.confidence}
                      feature={m.feature}
                      question={m.question}
                      projectId={context.projectId}
                      ragSessionId={m.ragSessionId}
                    />
                  ) : null}
                </div>
              </div>
            )
          )
        : null}

      {generating ? (
        <div className="spacer-ask-evans__row spacer-ask-evans__row--bot">
          <div className="spacer-ask-evans__avatar spacer-ask-evans__avatar--ae">AE</div>
          <div className="spacer-ask-evans__bubble spacer-ask-evans__bubble--bot spacer-ask-evans__thinking">
            <Spin size="small" />
            <span>Thinking… {elapsedSec}s</span>
            <button type="button" className="spacer-ask-evans__link-btn" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );

  const renderComposer = () => (
    <footer className="spacer-ask-evans__footer">
      {!aiEnabled ? (
        <div className="spacer-ask-evans__fallback">Ask Evans is temporarily unavailable</div>
      ) : null}
      <form
        className="spacer-ask-evans__composer"
        onSubmit={(e) => {
          e.preventDefault();
          handleSend();
        }}
      >
        <input
          ref={inputRef}
          type="text"
          className="spacer-ask-evans__input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={placeholder}
          disabled={generating || !aiEnabled || threadLoading}
          maxLength={2000}
          autoComplete="off"
        />
        <button
          type="submit"
          className="spacer-ask-evans__send"
          disabled={generating || !question.trim() || !aiEnabled || threadLoading}
          aria-label="Send"
        >
          {isPanel ? <ArrowRightOutlined /> : (
            <>
              <SendOutlined />
              <span>Send</span>
            </>
          )}
        </button>
      </form>
      <SpacerLogAiQuotaBar quota={quota} />
    </footer>
  );

  const renderChatMain = () => (
    <div className="spacer-ask-evans__main">
      {renderMessages()}
      {renderComposer()}
    </div>
  );

  const panelHistory = historyOpen ? (
    <div className="spacer-ask-evans__history-pop">
      <div className="spacer-ask-evans__history-pop-head">Recent chats</div>
      <div className="spacer-ask-evans__history-pop-list">
        {sessionsLoading ? (
          <div className="spacer-ask-evans__sidebar-empty">
            <Spin size="small" />
          </div>
        ) : null}
        {!sessionsLoading && !sessions.length ? (
          <p className="spacer-ask-evans__sidebar-empty">No chats yet. Ask a question to start.</p>
        ) : null}
        {!sessionsLoading
          ? sessions.slice(0, 20).map((s) => {
              const when = formatChatSessionDate(
                s.lastActive || s.updatedAt || s.createdAt
              );
              const msgs =
                s.messageCount != null ? ` · ${s.messageCount} messages` : '';
              return (
                <div
                  key={s.id}
                  className={`spacer-ask-evans__history-row${
                    String(s.id) === String(chatSessionId) ? ' is-active' : ''
                  }`}
                >
                  <button
                    type="button"
                    className="spacer-ask-evans__history-item"
                    onClick={() => handleSelectChat(s.id)}
                  >
                    <span className="spacer-ask-evans__sidebar-item-title">
                      {s.title || 'Untitled chat'}
                    </span>
                    <span className="spacer-ask-evans__sidebar-item-meta">
                      {when}
                      {msgs}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="spacer-ask-evans__history-del"
                    aria-label="Delete chat"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteChat(s.id);
                    }}
                  >
                    <CloseOutlined style={{ fontSize: 11 }} />
                  </button>
                </div>
              );
            })
          : null}
      </div>
    </div>
  ) : null;

  const viewTabs = (
    <div className="spacer-ask-evans__header-tabs" role="tablist" aria-label="Ask Evans views">
      <button
        type="button"
        role="tab"
        aria-selected={!isInsights}
        className={`spacer-ask-evans__header-tab${!isInsights ? ' spacer-ask-evans__header-tab--active' : ''}`}
        onClick={() => setView(VIEW.ASK)}
      >
        Chat
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={isInsights}
        className={`spacer-ask-evans__header-tab${isInsights ? ' spacer-ask-evans__header-tab--active' : ''}`}
        onClick={() => setView(VIEW.INSIGHTS)}
      >
        Insights
      </button>
    </div>
  );

  const floatingPanel =
    open && isPanel && !isInsights ? (
      <aside className="spacer-ask-evans-panel" role="dialog" aria-label="Ask Evans">
        <header className="spacer-ask-evans-panel__header">
          <div className="spacer-ask-evans-panel__brand">
            <Sparkles size={16} className="spacer-ask-evans-panel__spark" aria-hidden />
            <div>
              <div className="spacer-ask-evans-panel__title-row">
                <h2 className="spacer-ask-evans-panel__title">Ask Evans</h2>
                <span className="spacer-ask-evans-panel__beta">BETA</span>
              </div>
              <p className="spacer-ask-evans-panel__sub">
                Answers grounded in your Spacer Log data
              </p>
            </div>
          </div>
          <div className="spacer-ask-evans-panel__actions">
            <Tooltip title="New chat">
              <button type="button" className="spacer-ask-evans-panel__icon-btn" onClick={handleNewChat}>
                <PlusOutlined />
              </button>
            </Tooltip>
            <Tooltip title="Chat history">
              <button
                type="button"
                className="spacer-ask-evans-panel__icon-btn"
                onClick={() => {
                  setHistoryOpen((v) => !v);
                  refreshSessions();
                }}
              >
                <HistoryOutlined />
              </button>
            </Tooltip>
            <Tooltip title="Expand">
              <button type="button" className="spacer-ask-evans-panel__icon-btn" onClick={handleExpand}>
                <ExpandOutlined />
              </button>
            </Tooltip>
            <Tooltip title="Close">
              <button type="button" className="spacer-ask-evans-panel__icon-btn" onClick={handleClose}>
                <CloseOutlined />
              </button>
            </Tooltip>
          </div>
          {panelHistory}
        </header>
        <div className="spacer-ask-evans spacer-ask-evans--panel">{renderChatMain()}</div>
      </aside>
    ) : null;

  const pageHeader = (
    <header className="spacer-ask-evans__header">
      <div className="spacer-ask-evans__header-left">
        {isSpacerModal ? (
          <Tooltip title="Back to Spacer Log">
            <button
              type="button"
              className="spacer-ask-evans__back"
              onClick={handleClose}
              aria-label="Back to Spacer Log"
            >
              <ArrowLeftOutlined />
            </button>
          </Tooltip>
        ) : null}
        <div className="spacer-ask-evans__titles">
          <h2 className="spacer-ask-evans__title">Ask Evans</h2>
          <p className="spacer-ask-evans__subtitle">
            Ask questions about Spacer Log joints, downtime, and documents
          </p>
        </div>
      </div>
      <div className="spacer-ask-evans__header-right">
        {viewTabs}
        <div className="spacer-ask-evans__kb" aria-label="Knowledge base">
          <span className="spacer-ask-evans__kb-label">Knowledge base</span>
          <span className="spacer-ask-evans__kb-value">Spacer Log</span>
        </div>
      </div>
    </header>
  );

  const chatToolbar = (
    <div className="spacer-ask-evans__toolbar-row">
      <div className="spacer-ask-evans__header-actions">
        <button
          type="button"
          className="spacer-ask-evans__session-pdf"
          onClick={handleSessionSummaryPdf}
          disabled={!canSummary || summaryLoading}
        >
          <FilePdfOutlined />
          <span>{summaryLoading ? 'Preparing…' : 'Session summary'}</span>
        </button>
        {canOpenAiAdmin ? (
          <Dropdown
            overlay={
              <Menu
                onClick={({ key }) => {
                  if (key === 'ai') history.push('/admin/ai');
                  if (key === 'usage') history.push('/admin/ai-usage');
                }}
              >
                <Menu.Item key="ai">AI Configuration</Menu.Item>
                <Menu.Item key="usage">AI Usage</Menu.Item>
              </Menu>
            }
            trigger={['click']}
            placement="bottomRight"
            // Modal is z-index 1200 — dropdown must render above it.
            overlayStyle={{ zIndex: 1400 }}
            getPopupContainer={() => document.body}
          >
            <button
              type="button"
              className="spacer-ask-evans__settings-btn"
              aria-label="AI Configuration and AI Usage"
              title="AI Configuration & AI Usage"
            >
              <SettingOutlined />
            </button>
          </Dropdown>
        ) : null}
      </div>
    </div>
  );

  const pageContent = (
    <div className={`spacer-ask-evans spacer-ask-evans--fullscreen${defaultLayout === LAYOUT.PAGE ? ' spacer-ask-evans--route' : ''}`}>
      {isInsights ? (
        <SpacerLogAiInsightPanel
          open={open && isInsights}
          initialFeature={initialFeature}
          tabScope={tabScope}
          context={context}
          onQuota={setQuota}
          quotaBar={<SpacerLogAiQuotaBar quota={quota} />}
        >
          {({ sidebar, main }) => (
            <div className="spacer-ask-evans__workspace">
              {sidebar}
              <div className="spacer-ask-evans__stage">
                {pageHeader}
                <div className="spacer-ask-evans__panel">{main}</div>
              </div>
            </div>
          )}
        </SpacerLogAiInsightPanel>
      ) : (
        <div className="spacer-ask-evans__workspace">
          <SpacerLogAiChatSidebar
            sessions={sessions}
            activeId={chatSessionId}
            loading={sessionsLoading}
            onNewChat={handleNewChat}
            onSelect={handleSelectChat}
            onDelete={handleDeleteChat}
          />
          <div className="spacer-ask-evans__stage">
            {pageHeader}
            {chatToolbar}
            <div className="spacer-ask-evans__panel">
              {renderChatMain()}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // /AskEvans route — full page inside ModernLayout.
  const pageShell = isRoutePage && open ? pageContent : null;

  // Spacer Log — expand stays on this screen (app sidebar stays visible); Back closes.
  const expandModal = isSpacerModal && open ? (
    <Modal
      visible
      onCancel={handleClose}
      footer={null}
      width={typeof window !== 'undefined' ? window.innerWidth - 240 : '100%'}
      centered={false}
      destroyOnClose={false}
      className="spacer-ask-evans-modal"
      wrapClassName="spacer-ask-evans-modal-wrap"
      closable={false}
      maskClosable
      zIndex={1200}
      maskStyle={{ left: 240, backgroundColor: 'rgba(0, 0, 0, 0.35)' }}
      style={{ top: 0, paddingBottom: 0, maxWidth: 'none', margin: 0 }}
      bodyStyle={{ padding: 0, height: '100%', background: '#f5f6f8' }}
    >
      {pageContent}
    </Modal>
  ) : null;

  // FAB only when closed — expanded modal uses header Back; floating panel has its own Close.
  const showFab = !isRoutePage && !open && aiEnabled;
  const fabDisabled = !aiEnabled || quotaExhausted;

  return (
    <>
      {showFab ? (
        <button
          type="button"
          className="spacer-ask-evans-fab"
          onClick={handleFabClick}
          disabled={fabDisabled}
          aria-label={
            !aiEnabled
              ? 'Ask Evans unavailable'
              : quotaExhausted
                ? 'Ask Evans quota exhausted'
                : 'Ask Evans'
          }
          aria-expanded={false}
          title={
            !aiEnabled
              ? 'Ask Evans is temporarily unavailable'
              : quotaExhausted
                ? 'Monthly token quota used — resets on the 7th (UTC)'
                : undefined
          }
        >
          <Sparkles size={16} strokeWidth={2.25} aria-hidden />
          <span>Ask Evans</span>
        </button>
      ) : null}

      {floatingPanel}
      {expandModal}
      {pageShell}
    </>
  );
};

export default SpacerLogAiAskBar;
